import type Phaser from 'phaser';
import type { Direction, GameSettings } from '../types';

export const CONTROLLER_DEAD_ZONE = 0.18;

export type InputMethod = 'keyboard' | 'touch' | 'gamepad';
export type ControllerFamily = 'playstation' | 'generic';
export type ControllerStatus = 'not-connected' | 'connected' | 'active' | 'disconnected';
export type ControllerAction = 'confirm' | 'back' | 'power-up' | 'objective' | 'pause' | 'boost' | 'shield' | Direction;

export interface ControllerPrompts {
  confirm: string;
  back: string;
  powerUp: string;
  objective: string;
  pause: string;
  boost: string;
  shield: string;
}

export interface ControllerSnapshot {
  connected: boolean;
  status: ControllerStatus;
  id: string;
  mapping: string;
  buttonCount: number;
  axisCount: number;
  leftStickX: number;
  leftStickY: number;
  pressedButtons: string[];
  vibrationSupported: boolean;
  lastInput: InputMethod;
  lastInputAt: number;
  family: ControllerFamily;
  prompts: ControllerPrompts;
}

const GENERIC_PROMPTS: ControllerPrompts = {
  confirm: 'A', back: 'B', powerUp: 'X', objective: 'Y', pause: 'MENU', boost: 'RT', shield: 'LT',
};

const PLAYSTATION_PROMPTS: ControllerPrompts = {
  confirm: '✕', back: '○', powerUp: '□', objective: '△', pause: 'OPTIONS', boost: 'R2', shield: 'L2',
};

const BUTTON_NAMES = [
  'FACE SOUTH', 'FACE EAST', 'FACE WEST', 'FACE NORTH', 'L1 / LB', 'R1 / RB', 'L2 / LT', 'R2 / RT',
  'CREATE / VIEW', 'OPTIONS / MENU', 'L3', 'R3', 'D-PAD UP', 'D-PAD DOWN', 'D-PAD LEFT', 'D-PAD RIGHT',
  'PS / HOME', 'TOUCHPAD',
] as const;

const BUTTON_ACTIONS: Partial<Record<number, ControllerAction>> = {
  0: 'confirm', 1: 'back', 2: 'power-up', 3: 'objective', 6: 'shield', 7: 'boost', 9: 'pause',
  12: 'up', 13: 'down', 14: 'left', 15: 'right',
};

const INITIAL_SNAPSHOT: ControllerSnapshot = {
  connected: false,
  status: 'not-connected',
  id: 'No controller detected',
  mapping: 'none',
  buttonCount: 0,
  axisCount: 0,
  leftStickX: 0,
  leftStickY: 0,
  pressedButtons: [],
  vibrationSupported: false,
  lastInput: 'keyboard',
  lastInputAt: 0,
  family: 'generic',
  prompts: GENERIC_PROMPTS,
};

export function applyDeadZone(value: number, deadZone = CONTROLLER_DEAD_ZONE): number {
  const magnitude = Math.abs(value);
  if (!Number.isFinite(value) || magnitude <= deadZone) return 0;
  const normalized = Math.min(1, (magnitude - deadZone) / (1 - deadZone));
  return Math.sign(value) * normalized;
}

export function axesToDirection(x: number, y: number, deadZone = CONTROLLER_DEAD_ZONE): Direction | undefined {
  const adjustedX = applyDeadZone(x, deadZone);
  const adjustedY = applyDeadZone(y, deadZone);
  if (adjustedX === 0 && adjustedY === 0) return undefined;
  if (Math.abs(adjustedX) > Math.abs(adjustedY)) return adjustedX < 0 ? 'left' : 'right';
  return adjustedY < 0 ? 'up' : 'down';
}

export function buttonIndexToAction(index: number): ControllerAction | undefined {
  return BUTTON_ACTIONS[index];
}

export function isPlayStationController(id: string): boolean {
  if (/(xbox|xinput|microsoft)/i.test(id)) return false;
  return /(dualsense|dualshock|playstation|sony|054c)/i.test(id) || /^wireless controller$/i.test(id.trim());
}

export function promptsForController(id: string): ControllerPrompts {
  return isPlayStationController(id) ? PLAYSTATION_PROMPTS : GENERIC_PROMPTS;
}

export function controllerPromptFamily(connected: boolean, id: string): ControllerFamily | 'none' {
  if (!connected) return 'none';
  return isPlayStationController(id) ? 'playstation' : 'generic';
}

export function shouldPauseForDisconnect(screen: string, connected: boolean, activeInput: InputMethod, disconnectedWasActive: boolean): boolean {
  return screen === 'gameplay' && !connected && activeInput === 'gamepad' && disconnectedWasActive;
}

export class InputModeTracker {
  private recent: InputMethod = 'keyboard';

  constructor(private mode: GameSettings['controlMode'] = 'auto') {}

  configure(mode: GameSettings['controlMode']): InputMethod {
    this.mode = mode;
    return this.current();
  }

  note(method: InputMethod): InputMethod {
    this.recent = method;
    return this.current();
  }

  current(): InputMethod {
    return this.mode === 'auto' ? this.recent : this.mode;
  }
}

type StateListener = (snapshot: ControllerSnapshot) => void;
type ActionListener = (action: ControllerAction) => void;
type InputListener = (method: InputMethod) => void;

interface HapticActuator {
  playEffect?: (type: string, parameters: { duration: number; startDelay: number; strongMagnitude: number; weakMagnitude: number }) => Promise<unknown>;
  pulse?: (value: number, duration: number) => Promise<unknown>;
}

export class ControllerManager {
  private snapshot: ControllerSnapshot = { ...INITIAL_SNAPSHOT };
  private readonly stateListeners = new Set<StateListener>();
  private readonly actionListeners = new Set<ActionListener>();
  private readonly inputListeners = new Set<InputListener>();
  private readonly inputMode = new InputModeTracker();
  private previousButtons: boolean[] = [];
  private activeIndex = -1;
  private started = false;
  private lastSignature = '';
  private navigationDirection?: Direction;
  private nextNavigationAt = 0;

  start(): void {
    if (this.started || typeof window === 'undefined') return;
    this.started = true;
    window.addEventListener('gamepadconnected', this.handleConnected);
    window.addEventListener('gamepaddisconnected', this.handleDisconnected);
    window.addEventListener('keydown', this.handleKeyboard, { passive: true });
    window.addEventListener('pointerdown', this.handlePointer, { passive: true });
    this.poll();
  }

  configureMode(mode: GameSettings['controlMode']): void {
    const method = this.inputMode.configure(mode);
    this.inputListeners.forEach((listener) => listener(method));
    this.emitState(true);
  }

  attachPhaser(plugin: Phaser.Input.Gamepad.GamepadPlugin | null): () => void {
    if (!plugin) return () => undefined;
    const connected = (pad: Phaser.Input.Gamepad.Gamepad): void => this.reportPhaserConnection(pad.id);
    const disconnected = (): void => this.scanForGamepad(true);
    plugin.on('connected', connected);
    plugin.on('disconnected', disconnected);
    return () => { plugin.off('connected', connected); plugin.off('disconnected', disconnected); };
  }

  reportPhaserConnection(id: string): void {
    this.scanForGamepad();
    if (!this.snapshot.connected) {
      this.snapshot = { ...this.snapshot, connected: true, status: 'connected', id, family: isPlayStationController(id) ? 'playstation' : 'generic', prompts: promptsForController(id) };
      this.emitState(true);
    }
  }

  getSnapshot(): ControllerSnapshot { return { ...this.snapshot, pressedButtons: [...this.snapshot.pressedButtons], prompts: { ...this.snapshot.prompts } }; }
  getActiveInput(): InputMethod { return this.inputMode.current(); }

  getMovementDirection(): Direction | undefined {
    const pad = this.getActiveGamepad();
    if (!pad) return undefined;
    if (pad.buttons[12]?.pressed) return 'up';
    if (pad.buttons[13]?.pressed) return 'down';
    if (pad.buttons[14]?.pressed) return 'left';
    if (pad.buttons[15]?.pressed) return 'right';
    return axesToDirection(pad.axes[0] ?? 0, pad.axes[1] ?? 0);
  }

  noteInput(method: InputMethod): void {
    this.inputMode.note(method);
    if (method === 'gamepad' && this.snapshot.connected && this.snapshot.status !== 'active') this.snapshot = { ...this.snapshot, status: 'active' };
    if (method !== 'gamepad' && this.snapshot.connected && this.snapshot.status === 'active') this.snapshot = { ...this.snapshot, status: 'connected' };
    if (method !== 'gamepad' && !this.snapshot.connected && this.snapshot.status === 'disconnected') this.snapshot = { ...this.snapshot, status: 'not-connected' };
    if (method !== this.snapshot.lastInput || Date.now() - this.snapshot.lastInputAt > 200) this.emitInput(method);
  }

  onState(listener: StateListener): () => void { this.stateListeners.add(listener); listener(this.getSnapshot()); return () => this.stateListeners.delete(listener); }
  onAction(listener: ActionListener): () => void { this.actionListeners.add(listener); return () => this.actionListeners.delete(listener); }
  onInput(listener: InputListener): () => void { this.inputListeners.add(listener); return () => this.inputListeners.delete(listener); }

  async vibrate(duration: number, strongMagnitude = 0.25, weakMagnitude = 0.45): Promise<boolean> {
    const pad = this.getActiveGamepad();
    const actuator = pad ? (pad.vibrationActuator as unknown as HapticActuator | null) : null;
    if (!actuator) return false;
    try {
      if (actuator.playEffect) {
        await actuator.playEffect('dual-rumble', { duration, startDelay: 0, strongMagnitude, weakMagnitude });
        return true;
      }
      if (actuator.pulse) { await actuator.pulse(Math.max(strongMagnitude, weakMagnitude), duration); return true; }
    } catch { return false; }
    return false;
  }

  private readonly handleConnected = (event: GamepadEvent): void => {
    this.connectGamepad(event.gamepad);
  };

  private readonly handleDisconnected = (event: GamepadEvent): void => {
    if (event.gamepad.index !== this.activeIndex) return;
    const disconnectedWasActive = this.snapshot.status === 'active' && this.snapshot.lastInput === 'gamepad';
    this.activeIndex = -1;
    this.previousButtons = [];
    this.snapshot = {
      ...this.snapshot, connected: false, status: disconnectedWasActive ? 'disconnected' : 'not-connected', pressedButtons: [], leftStickX: 0, leftStickY: 0,
      vibrationSupported: false,
    };
    this.emitState(true);
    this.scanForGamepad(true);
  };

  private readonly handleKeyboard = (): void => this.noteInput('keyboard');
  private readonly handlePointer = (event: PointerEvent): void => { if (event.pointerType === 'touch') this.noteInput('touch'); };

  private poll = (): void => {
    this.scanForGamepad();
    const pad = this.getActiveGamepad();
    if (pad) this.readGamepad(pad);
    window.requestAnimationFrame(this.poll);
  };

  private scanForGamepad(force = false): void {
    if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') return;
    const pads = [...navigator.getGamepads()].filter((pad): pad is Gamepad => Boolean(pad?.connected));
    const current = pads.find((pad) => pad.index === this.activeIndex);
    if (current) return;
    const next = pads[0];
    if (next) this.connectGamepad(next);
    else if (force && this.activeIndex >= 0) this.handleDisconnected({ gamepad: { index: this.activeIndex } } as GamepadEvent);
  }

  private connectGamepad(pad: Gamepad): void {
    const changed = this.activeIndex !== pad.index || !this.snapshot.connected;
    this.activeIndex = pad.index;
    this.previousButtons = pad.buttons.map(() => false);
    const family: ControllerFamily = isPlayStationController(pad.id) ? 'playstation' : 'generic';
    this.snapshot = {
      ...this.snapshot,
      connected: true,
      status: 'connected',
      id: pad.id || 'Standard gamepad',
      mapping: pad.mapping || 'vendor-specific',
      buttonCount: pad.buttons.length,
      axisCount: pad.axes.length,
      family,
      prompts: promptsForController(pad.id),
      vibrationSupported: Boolean(pad.vibrationActuator),
    };
    if (changed) this.emitState(true);
  }

  private readGamepad(pad: Gamepad): void {
    const now = performance.now();
    const pressed = pad.buttons.map((button, index) => ({ button, index })).filter(({ button }) => button.pressed || button.value > 0.35);
    const direction = this.getMovementDirection();
    const hasActivity = pressed.length > 0 || direction !== undefined;
    if (hasActivity) this.noteInput('gamepad');

    pressed.forEach(({ index }) => {
      if (!this.previousButtons[index]) {
        const action = buttonIndexToAction(index);
        if (action) this.actionListeners.forEach((listener) => listener(action));
      }
    });

    const directionButtonIndex = direction ? 12 + ['up', 'down', 'left', 'right'].indexOf(direction) : -1;
    const dpadPressed = directionButtonIndex >= 12 && Boolean(pad.buttons[directionButtonIndex]?.pressed);
    if (direction) {
      const changedDirection = direction !== this.navigationDirection;
      const dpadEdgeAlreadyEmitted = dpadPressed && !this.previousButtons[directionButtonIndex];
      if ((changedDirection || now >= this.nextNavigationAt) && !dpadEdgeAlreadyEmitted) {
        this.actionListeners.forEach((listener) => listener(direction));
      }
      if (changedDirection || now >= this.nextNavigationAt) {
        this.navigationDirection = direction;
        this.nextNavigationAt = now + (changedDirection ? 300 : 155);
      }
    } else {
      this.navigationDirection = undefined;
      this.nextNavigationAt = 0;
    }

    this.previousButtons = pad.buttons.map((button) => button.pressed || button.value > 0.35);
    this.snapshot = {
      ...this.snapshot,
      connected: true,
      status: hasActivity ? 'active' : this.snapshot.status === 'active' && this.snapshot.lastInput === 'gamepad' ? 'active' : 'connected',
      leftStickX: applyDeadZone(pad.axes[0] ?? 0),
      leftStickY: applyDeadZone(pad.axes[1] ?? 0),
      pressedButtons: pressed.map(({ index }) => BUTTON_NAMES[index] ?? `BUTTON ${index}`),
      vibrationSupported: Boolean(pad.vibrationActuator),
    };
    this.emitState();
  }

  private getActiveGamepad(): Gamepad | null {
    if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function' || this.activeIndex < 0) return null;
    return navigator.getGamepads()[this.activeIndex] ?? null;
  }

  private emitInput(method: InputMethod): void {
    this.snapshot = { ...this.snapshot, lastInput: method, lastInputAt: Date.now() };
    this.inputListeners.forEach((listener) => listener(method));
    this.emitState(true);
  }

  private emitState(force = false): void {
    const signature = JSON.stringify([
      this.snapshot.connected, this.snapshot.status, this.snapshot.id, this.snapshot.mapping, this.snapshot.buttonCount,
      this.snapshot.axisCount, this.snapshot.leftStickX.toFixed(2), this.snapshot.leftStickY.toFixed(2),
      this.snapshot.pressedButtons, this.snapshot.vibrationSupported, this.snapshot.lastInput,
    ]);
    if (!force && signature === this.lastSignature) return;
    this.lastSignature = signature;
    const snapshot = this.getSnapshot();
    this.stateListeners.forEach((listener) => listener(snapshot));
  }
}

export const controllerManager = new ControllerManager();
